/**
 * The one answer to "did this payment attempt fail to move money?"
 * (PD-15305).
 *
 * Before this, three different classifiers each checked the single literal
 * 'Failed': a row carrying 'Declined', 'Error', or Authorize.Net's 'Held for
 * Review' was netted in as a real payment and could close an invoice nothing
 * was collected for. The failure vocabulary already existed in the
 * payment-method failure tracker; this shares it so the invoice math, the
 * money-moved classifier, the reconciliation report, and the failure tracker
 * cannot disagree.
 *
 * 'Held for Review' is included deliberately: it is not a terminal failure,
 * but the money has NOT moved — if the processor later approves it, the row's
 * status changes and it counts from then on. Counting it before approval is
 * the same defect as counting a decline.
 */
export const PAYMENT_FAILURE_STATUSES: ReadonlyArray<string> = [
	'Failed',
	'Declined',
	'Error',
];

/** A terminal gateway refusal — the vocabulary the failure tracker uses. */
export function isPaymentFailureStatus(
	status: string | null | undefined,
): boolean {
	return status != null && PAYMENT_FAILURE_STATUSES.includes(status);
}

/**
 * Statuses under which the money has NOT been collected: the terminal
 * failures plus 'Held for Review', which is pending rather than failed —
 * the wider set money math must use, while the failure tracker (which
 * penalizes the payment method) sticks to the terminal failures alone.
 */
export const PAYMENT_NOT_COLLECTED_STATUSES: ReadonlyArray<string> = [
	...PAYMENT_FAILURE_STATUSES,
	'Held for Review',
];

export function isPaymentNotCollectedStatus(
	status: string | null | undefined,
): boolean {
	return status != null && PAYMENT_NOT_COLLECTED_STATUSES.includes(status);
}
